import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type DashboardFocusTypeParam =
  | "page"
  | "idea"
  | "audit_job"
  | "ga_insight";
export type DashboardContextTypeParam = "persona" | "offer" | "policy";

export interface DashboardUrlParams {
  sidebarOpen: boolean;
  gaEdit: boolean;
  gscEdit: boolean;
  focusType: DashboardFocusTypeParam | null;
  focusId: string | null;
  contextType: DashboardContextTypeParam | null;
  contextId: string | null;
}

const FOCUS_TYPES = new Set<DashboardFocusTypeParam>([
  "page",
  "idea",
  "audit_job",
  "ga_insight",
]);
const CONTEXT_TYPES = new Set<DashboardContextTypeParam>([
  "persona",
  "offer",
  "policy",
]);

const readEnumParam = <T extends string>(
  params: URLSearchParams,
  key: string,
  allowed: Set<T>,
): T | null => {
  const value = params.get(key);
  return value && allowed.has(value as T) ? (value as T) : null;
};

/**
 * Centralized hook for handling dashboard-specific URL parameters
 * Manages sidebar state, Google Analytics edit mode, and other dashboard params
 */
export const useDashboardUrlParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive params directly from URL search params (external source of truth)
  const params = useMemo<DashboardUrlParams>(() => {
    const sidebarOpenParam = searchParams.get("sidebarOpen");
    const gaEditParam = searchParams.get("gaEdit");
    const gscEditParam = searchParams.get("gscEdit");

    return {
      sidebarOpen: sidebarOpenParam?.toLowerCase() === "true",
      gaEdit: gaEditParam?.toLowerCase() === "true",
      gscEdit: gscEditParam?.toLowerCase() === "true",
      focusType: readEnumParam(searchParams, "focusType", FOCUS_TYPES),
      focusId: searchParams.get("focusId"),
      contextType: readEnumParam(searchParams, "contextType", CONTEXT_TYPES),
      contextId: searchParams.get("contextId"),
    };
  }, [searchParams]);

  /**
   * Clear specific parameters from the URL
   */
  const clearParams = useCallback(
    (paramNames: (keyof DashboardUrlParams)[]) => {
      const next = new URLSearchParams(searchParams);
      paramNames.forEach((paramName) => {
        next.delete(paramName);
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /**
   * Clear all dashboard parameters from the URL
   */
  const clearAllParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("sidebarOpen");
    next.delete("gaEdit");
    next.delete("gscEdit");
    next.delete("focusType");
    next.delete("focusId");
    next.delete("contextType");
    next.delete("contextId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return {
    params,
    clearParams,
    clearAllParams,
  };
};
